<template>
  <a-modal width="800px" :visible="visible" title="Create Written Warning" @cancel="closeModal">
    <div class="grid">
      <b class="col-6">Associate</b>
      <grouped-associate-info
        class="col-6"
        :first-name="associate.first_name"
        :last-name="associate.last_name"
        :role="associate?.role?.name"
        :associate-number="associate.associate_number"
        :unverified="!associate.is_verified"
        :photo-url='associate.photo_url'
      />

      <b class="col-6">Date <span class="required-field">*</span></b>
      <a-create_date-picker class="col-6" v-model="create_date" :format="dateFormat" />

      <b class="col-6">Follow up review days <span class="required-field">*</span></b>
      <div class="col-6">
        <a-input-number v-model="follow_up_review_date_days" :min="1" :max="31" placeholder="Follow up review days" />
      </div>

      <b class="col-6">Violation category <span class="required-field">*</span></b>
      <a-select
        v-model="violation_category"
        class="col-6"
        placeholder="Select category"
        :options="violationCategoryTypes"
      />

      <b class="col-6">Reason for counselling/corrective action</b>
      <a-auto-complete
        v-model="reason_for_action"
        class="col-6 dropdown-custom"
        @search="value => handleSearch(value, 'reason_for_action')"
        @select="value => handleSelect(value, 'reason_for_action')"
        @change="value => handleChange(value, 'reason_for_action')"
        placeholder="Type to search"
      >
        <template slot="dataSource">
          <a-select-option
            class="custom-dropdown-item"
            v-for="item in dataSource.reason_for_action"
            :key="item"
            :title="item"
          >
            {{ item }}
          </a-select-option>
        </template>
      </a-auto-complete>

      <b class="col-6">Expected level of performance</b
      ><a-auto-complete
        v-model="expected_level_of_performance"
        class="col-6 dropdown-custom"
        @search="value => handleSearch(value, 'expected_level_of_performance')"
        @select="value => handleSelect(value, 'expected_level_of_performance')"
        @change="value => handleChange(value, 'expected_level_of_performance')"
        placeholder="Type to search"
      >
        <template slot="dataSource">
          <a-select-option
            :style="{ color: 'red' }"
            class="custom-dropdown-item"
            v-for="item in dataSource.expected_level_of_performance"
            :key="item"
            :title="item"
          >
            {{ item }}
          </a-select-option>
        </template>
      </a-auto-complete>
      <div class="col-12">
        <attachments-field :files="files" @save="saveAttachmentsIds" :with-title="true" />
      </div>
      {{ signature_id }}

      <div class="col-12">
        <signature
          :key="signatureKey"
          checkbox-label="I agree that all data is true and correct"
          ref="signatureRef"
          :available-signature="null"
          @checked="setSignatureCheckboxValue"
          @save-signature="setSignatureId"
        />
      </div>
    </div>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="closeModal">Close</a-button>
        <a-button type="primary" :disabled="disabled" :loading="loading" @click="createWriteUp"> Send </a-button>
      </div></template
    >
  </a-modal>
</template>

<script>
import { Modal, DatePicker, Select, InputNumber, Button, notification, AutoComplete } from 'ant-design-vue'
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import { violationCategories } from '@/const'
import { LEVEL_OF_PERFORMANCE, WRITEUP_REASON } from '@/store/modules/profile/constants'
import { createNamespacedHelpers } from 'vuex'
import Signature from '@/components/signature.vue'
import ShiftFormAttachments from '@/components/form-attachments.vue'
import { deepCopy } from '@/utils/deep-copy'

const { mapState: profileState, mapActions: profileActions } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-modal': Modal,
    'a-create_date-picker': DatePicker,
    'a-select': Select,
    'a-input-number': InputNumber,
    'a-button': Button,
    'a-auto-complete': AutoComplete,
    'a-select-option': Select.Option,
    'attachments-field': ShiftFormAttachments,
    'grouped-associate-info': GroupedAssociateInfo,
    signature: Signature
  },
  props: {
    visible: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      create_date: undefined,
      follow_up_review_date_days: undefined,
      violation_category: undefined,
      reason_for_action: undefined,
      expected_level_of_performance: undefined,

      loading: false,

      dataSource: {
        reason_for_action: Object.values(WRITEUP_REASON),
        expected_level_of_performance: Object.values(LEVEL_OF_PERFORMANCE)
      },

      availableSources: {
        reason_for_action: Object.values(WRITEUP_REASON),
        expected_level_of_performance: Object.values(LEVEL_OF_PERFORMANCE)
      },

      checked: false,
      signature_id: undefined,

      signatureKey: 0,

      attachmentsIds: [],
      files: []
    }
  },

  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    dateFormat() {
      return this.$store.state.applicationState.dateFormat
    },
    violationCategoryTypes() {
      return violationCategories
    },
    serverDateFormat() {
      return this.$store.state.applicationState.serverDateFormat
    },
    disabled() {
      return (
        !this.create_date ||
        !this.follow_up_review_date_days ||
        !this.violation_category ||
        !this.checked
      )
    }
  },
  methods: {
    ...profileActions(['postAssociateWriteUp']),

    closeModal() {
      this.create_date = undefined
      this.follow_up_review_date_days = undefined
      this.violation_category = undefined
      this.reason_for_action = undefined
      this.expected_level_of_performance = undefined
      this.signature_id = undefined
      this.signatureKey += 1
      this.files = []
      this.attachmentsIds = []
      this.$emit('update:visible', false)
    },

    showNotification(type, message, description) {
      notification[type]({
        message: message,
        description: description
      })
    },

    saveAttachmentsIds(ids) {
      this.attachmentsIds = deepCopy(ids)
    },

    setSignatureCheckboxValue(val) {
      this.checked = val
    },

    setSignatureId(signature_id) {
      this.signature_id = signature_id
    },

    getFilteredOptions(inputText, key) {
      if (!inputText) return []

      return this.availableSources[key].filter(item => item.toLowerCase().includes(inputText.toLowerCase()))
    },

    handleSearch(value, key) {
      this[key] = value
      this.dataSource[key] = this.getFilteredOptions(value, key)
    },

    handleSelect(value, key) {
      this[key] = value

      if (key !== 'reason_for_action' || this.expected_level_of_performance) return

      const reasonKey = Object.keys(WRITEUP_REASON).find(item => WRITEUP_REASON[item] === value)

      if (reasonKey && LEVEL_OF_PERFORMANCE[reasonKey]) {
        this.expected_level_of_performance = LEVEL_OF_PERFORMANCE[reasonKey]
      }
    },

    handleChange(value, key) {
      this[key] = value
    },

    async createWriteUp() {
      this.loading = true

      try {
        await this.$refs.signatureRef.saveSignature()

        if (!this.signature_id) {
          return
        }

        const payload = {
          associate_number: this.associate.associate_number,
          signature_id: this.signature_id,
          manager_scopes: this.associate.manager_scopes,
          writeup: {
            create_date: this.create_date.format(this.serverDateFormat),
            follow_up_review_date_days: this.follow_up_review_date_days,
            violation_category: this.violation_category,
            reason_for_action: this.reason_for_action,
            expected_level_of_performance: this.expected_level_of_performance,
            attached_files: this.attachmentsIds
          }
        }

        await this.postAssociateWriteUp(payload)

        this.showNotification('success', 'Success', 'Written Warning Created Successfully')

        this.closeModal()
      } catch (error) {
        this.showNotification('error', 'Creating Error', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.ant-calendar-picker {
  margin: 0;
}
.ant-input-number {
  width: 100%;
}
</style>

<style>
.custom-dropdown-item {
  overflow: visible !important;
  white-space: normal !important;
}
</style>
